import type { Updater } from '@tanstack/react-query'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { toTitleCase } from '@mobi/utils/string'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { promptUserAndReturnDecision } from '@core/Components/Modal/Components/PromptForUserDecision/helpers'
import type { BlackbookDetailsResponse, BlackbookEntry } from '@core/Areas/Blackbook/types'
import { trackBlackbookEntryDeleteCanceled, trackBlackbookEntryDeleted } from '../analytics'
import { deleteBlackbookEntry } from './api'

const enum LocalConstants {
  RemoveRunnerModalId = 'remove-blackbook-runner',
}

export const deleteRunnerFromBlackbook = async ({ id, name, type }: DeleteRunnerArgs) => {
  let subtitle = ''

  if (type === 'Jockeys') {
    subtitle = `Are you sure you want to remove the jockey "${toTitleCase(
      name
    )}" from your Blackbook?`
  } else {
    subtitle = `Are you sure you want to remove "${toTitleCase(name)}" from your Blackbook?`
  }

  const decision = await promptUserAndReturnDecision({
    id: LocalConstants.RemoveRunnerModalId,
    testId: 'blackboook-remove-runner',
    title: 'Remove from Blackbook',
    subtitle,
    buttons: [
      { label: 'Cancel', color: 'secondary' },
      { label: 'Yes, delete', color: 'danger' },
    ],
  })

  if (decision === 'Yes, delete') {
    try {
      await deleteBlackbookEntry(id)

      addToast({
        id: 'blackbook-entry-delete',
        title: 'Blackbook entry deleted',
        icon: 'success',
      })

      invalidateQueriesOnSuccessfulDelete(id)

      trackBlackbookEntryDeleted(type)
    } catch {
      addToast({
        id: 'blackbook-runner-delete-error',
        title: 'Unable to Delete Runner',
        icon: 'error',
        timeoutMs: Infinity,
      })
    }
  } else {
    trackBlackbookEntryDeleteCanceled()
  }
}

// =============
// Local Helpers
// =============

function invalidateQueriesOnSuccessfulDelete(id: BlackbookEntry['Id']) {
  queryClient.invalidateQueries({ queryKey: [queryKeys.blackbookDetails], refetchActive: false })
  queryClient.setQueryData<BlackbookCachedQuery>(
    [queryKeys.blackbookDetails],
    removeRunnerFromBlackbookCache(id)
  )
  queryClient.invalidateQueries({
    queryKey: [queryKeys.blackbookRunnersBase],
    refetchInactive: true,
  })
}

const removeRunnerFromBlackbookCache: (
  id: number
) => Updater<BlackbookCachedQuery, BlackbookCachedQuery> = id => oldData => {
  if (!oldData) return undefined
  const newEntries = oldData?.Entries?.filter(entry => entry.Id !== id) || null
  return { ...oldData, Entries: newEntries }
}

// =====
// Types
// =====

type BlackbookCachedQuery = BlackbookDetailsResponse | undefined

type DeleteRunnerArgs = {
  id: BlackbookEntry['Id']
  name: string
  type: BlackbookEntry['Code']
}
